// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

import { pickFromCamera } from '@/features/tour-photos/pickPhotos';
import { newPhotoId } from '@/features/tour-photos/newPhotoId';
import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';

import {
  enqueueStakeUpload,
  retryStakeUpload,
  setStakeUploadAuthToken,
  subscribeToStakeUploads,
} from './stakeUploadQueue';
import { MAX_STAKE_ACCURACY_M, useStakeCapture } from './useStakeCapture';
import { STAKE_KINDS, type StakeKind, type StakeObservation, type StakeUploadEntry } from './types';

export type StakeCaptureScreenProps = {
  courseId: string;
  holesCount: number;
  token: string | null | undefined;
};

/**
 * Record boundary stakes: stand at the stake, face out, take the photo.
 *
 * The shutter is held until the averaged fix is sharp enough, and the position and heading
 * are frozen the instant it is pressed — before the camera opens — because a mapper turns the
 * phone to frame the picture and the compass would follow. The photo is optional: a stake in
 * the rain is still a stake.
 */
export function StakeCaptureScreen({ courseId, holesCount, token }: StakeCaptureScreenProps) {
  const { t } = useI18n();
  const capture = useStakeCapture({ enabled: Platform.OS !== 'web' });
  const [hole, setHole] = useState(1);
  const [kind, setKind] = useState<StakeKind>('out-of-bounds');
  const [entries, setEntries] = useState<StakeUploadEntry[]>([]);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    setStakeUploadAuthToken(token);
  }, [token]);

  useEffect(() => subscribeToStakeUploads(setEntries), []);

  const sessionEntries = useMemo(
    () => entries.filter((entry) => entry.job.stake.courseId === courseId).slice().reverse(),
    [courseId, entries],
  );

  const mark = async (withPhoto: boolean) => {
    const frozen = capture.snapshot();
    if (!frozen) {
      setMessage(t('courseStakes.accuracyTooLow', { meters: '?', limit: MAX_STAKE_ACCURACY_M }));
      return;
    }
    setMessage(null);

    const stake: StakeObservation = {
      id: newPhotoId(),
      courseId,
      holeNumber: hole,
      stakeKind: kind,
      lat: frozen.fix.lat,
      lon: frozen.fix.lon,
      accuracyMeters: Math.round(frozen.fix.accuracyMeters * 10) / 10,
      headingDegrees: frozen.heading ? Math.round(frozen.heading.degrees * 10) / 10 : null,
      headingAccuracy: frozen.heading?.accuracy ?? null,
      capturedAt: new Date().toISOString(),
    };

    if (!withPhoto) {
      enqueueStakeUpload({ stake, photo: null });
      return;
    }

    const outcome = await pickFromCamera();
    if (outcome.kind === 'cancelled') return;
    if (outcome.kind === 'permission-denied') {
      setMessage(t('courseStakes.cameraDenied'));
      return;
    }
    if (outcome.kind === 'rejected') {
      setMessage(t('courseStakes.photoRejected'));
      return;
    }
    enqueueStakeUpload({ stake, photo: outcome.photos[0] ?? null });
  };

  const kindLabel = (value: StakeKind) => t(`courseStakes.kind_${value}`);

  if (Platform.OS === 'web') {
    return (
      <Text style={styles.paragraph} testID="course-stakes-native-only">
        {t('courseStakes.nativeOnly')}
      </Text>
    );
  }

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <Text style={styles.paragraph}>{t('courseStakes.intro')}</Text>

      <View style={styles.row}>
        <Text style={styles.label}>{t('courseStakes.hole')}</Text>
        <View style={styles.stepper}>
          <Pressable
            testID="course-stakes-hole-down"
            accessibilityRole="button"
            style={styles.stepButton}
            onPress={() => setHole((h) => Math.max(0, h - 1))}
          >
            <Text style={styles.stepText}>−</Text>
          </Pressable>
          <Text style={styles.stepValue} testID="course-stakes-hole">
            {hole === 0 ? t('courseStakes.holeUnknown') : String(hole)}
          </Text>
          <Pressable
            testID="course-stakes-hole-up"
            accessibilityRole="button"
            style={styles.stepButton}
            onPress={() => setHole((h) => Math.min(Math.max(holesCount, 1), h + 1))}
          >
            <Text style={styles.stepText}>+</Text>
          </Pressable>
        </View>
      </View>

      <View style={styles.chips}>
        {STAKE_KINDS.map((value) => (
          <Pressable
            key={value}
            testID={`course-stakes-kind-${value}`}
            accessibilityRole="button"
            accessibilityState={{ selected: value === kind }}
            style={[styles.chip, value === kind && styles.chipSelected]}
            onPress={() => setKind(value)}
          >
            <Text style={[styles.chipText, value === kind && styles.chipTextSelected]}>{kindLabel(value)}</Text>
          </Pressable>
        ))}
      </View>

      <View style={styles.readout}>
        <Text style={styles.label}>{t('courseStakes.position')}</Text>
        <Text style={styles.value} testID="course-stakes-fix">
          {capture.permission === 'denied'
            ? t('courseStakes.permissionDenied')
            : capture.fix === null
              ? t('courseStakes.waitingForFix')
              : capture.ready
                ? t('courseStakes.accuracy', { meters: capture.fix.accuracyMeters.toFixed(1) })
                : t('courseStakes.accuracyTooLow', {
                    meters: capture.fix.accuracyMeters.toFixed(1),
                    limit: MAX_STAKE_ACCURACY_M,
                  })}
        </Text>
        <Text style={styles.label}>{t('courseStakes.heading')}</Text>
        <Text style={styles.value} testID="course-stakes-heading">
          {capture.heading
            ? t('courseStakes.headingDegrees', { degrees: Math.round(capture.heading.degrees) })
            : t('courseStakes.headingUnavailable')}
        </Text>
      </View>

      <Pressable
        testID="course-stakes-shutter"
        accessibilityRole="button"
        accessibilityState={{ disabled: !capture.ready }}
        style={[styles.shutter, !capture.ready && styles.shutterDisabled]}
        disabled={!capture.ready}
        onPress={() => void mark(true)}
      >
        <Text style={styles.shutterText}>{t('courseStakes.takePhoto')}</Text>
      </Pressable>
      <Pressable
        testID="course-stakes-mark-without-photo"
        accessibilityRole="button"
        accessibilityState={{ disabled: !capture.ready }}
        style={[styles.secondary, !capture.ready && styles.shutterDisabled]}
        disabled={!capture.ready}
        onPress={() => void mark(false)}
      >
        <Text style={styles.secondaryText}>{t('courseStakes.markWithoutPhoto')}</Text>
      </Pressable>

      {message ? (
        <Text style={styles.message} testID="course-stakes-message">
          {message}
        </Text>
      ) : null}

      <Text style={styles.label}>{t('courseStakes.recordedCount', { count: sessionEntries.length })}</Text>
      {sessionEntries.map((entry) => (
        <View key={entry.job.stake.id} style={styles.entry} testID={`course-stakes-entry-${entry.job.stake.id}`}>
          <Text style={styles.value}>
            {t('courseStakes.stakeRow', {
              hole: entry.job.stake.holeNumber === 0 ? t('courseStakes.holeUnknown') : entry.job.stake.holeNumber,
              kind: kindLabel(entry.job.stake.stakeKind),
            })}
          </Text>
          <Text style={styles.status}>
            {entry.status === 'done'
              ? t('courseStakes.statusDone')
              : entry.status === 'failed'
                ? t('courseStakes.statusFailed', { error: entry.error ?? '' })
                : t('courseStakes.statusPending')}
          </Text>
          {entry.status === 'failed' ? (
            <Pressable
              testID={`course-stakes-retry-${entry.job.stake.id}`}
              accessibilityRole="button"
              style={styles.secondary}
              onPress={() => retryStakeUpload(entry.job.stake.id)}
            >
              <Text style={styles.secondaryText}>{t('courseStakes.retry')}</Text>
            </Pressable>
          ) : null}
        </View>
      ))}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { padding: 16, gap: 12 },
  paragraph: { fontSize: 15, color: appColors.text.secondary },
  row: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  label: { fontSize: 13, fontWeight: '600', color: appColors.text.secondary },
  value: { fontSize: 16, color: appColors.text.primary },
  stepper: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  stepButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: appColors.surface.elevated,
  },
  stepText: { fontSize: 22, color: appColors.text.primary },
  stepValue: { fontSize: 22, fontWeight: '700', minWidth: 60, textAlign: 'center', color: appColors.text.primary },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  chip: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 16, backgroundColor: appColors.surface.elevated },
  chipSelected: { backgroundColor: appColors.accent.primary },
  chipText: { color: appColors.text.primary },
  chipTextSelected: { color: appColors.text.inverse, fontWeight: '600' },
  readout: { gap: 4, padding: 12, borderRadius: 12, backgroundColor: appColors.surface.elevated },
  shutter: { padding: 18, borderRadius: 14, alignItems: 'center', backgroundColor: appColors.accent.primary },
  shutterDisabled: { opacity: 0.4 },
  shutterText: { fontSize: 18, fontWeight: '700', color: appColors.text.inverse },
  secondary: { padding: 12, borderRadius: 12, alignItems: 'center', backgroundColor: appColors.surface.elevated },
  secondaryText: { fontSize: 15, fontWeight: '600', color: appColors.text.primary },
  message: { fontSize: 14, color: appColors.text.warm },
  entry: { gap: 4, paddingVertical: 8, borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: appColors.border.subtle },
  status: { fontSize: 13, color: appColors.text.secondary },
});
